// Terraform lessons - base set (20 lessons)
const baseTerraformLessons = [
  {
    title: "Terraform Basics",
    description: "Terraform uses commands to manage infrastructure. Let's start with the command that initializes a new Terraform working directory.",
    task: "Enter the command to initialize a Terraform working directory.",
    hint: "This command downloads provider plugins and sets up the backend.",
    example: "terraform <command>",
    answer: "terraform init",
    tips: [
      "This is typically the first command you run in a new Terraform project",
      "It creates a .terraform directory to store plugins and modules"
    ],
    commandRef: {
      command: "terraform init",
      description: "Initializes a Terraform working directory by downloading provider plugins, setting up the backend for state storage, and preparing the directory for other Terraform commands.",
      syntax: "terraform init [options]",
      options: [
        { flag: "-backend=false", description: "Skip backend configuration during initialization" },
        { flag: "-backend-config=path", description: "Path to a file with backend configuration" },
        { flag: "-upgrade", description: "Upgrade modules and plugins to the latest versions" },
        { flag: "-reconfigure", description: "Reconfigure the backend, ignoring any saved configuration" },
        { flag: "-migrate-state", description: "Migrate state from one backend to another" }
      ],
      examples: [
        { code: "terraform init", description: "Initialize the current directory with default settings" },
        { code: "terraform init -upgrade", description: "Initialize and upgrade all plugins to latest versions" },
        { code: "terraform init -backend-config=backend.hcl", description: "Initialize with a specific backend configuration file" }
      ]
    }
  },
  {
    title: "Terraform Planning",
    description: "Before applying changes, it's good practice to see what Terraform will do.",
    task: "Enter the command to create an execution plan.",
    hint: "This command shows what changes Terraform will make to your infrastructure.",
    example: "terraform <command>",
    answer: "terraform plan",
    tips: [
      "This command doesn't make any changes to your infrastructure",
      "It's a way to verify that the changes match your expectations"
    ],
    commandRef: {
      command: "terraform plan",
      description: "Creates an execution plan showing what actions Terraform will take to change your infrastructure to match the configuration. This is a dry-run that doesn't make any actual changes.",
      syntax: "terraform plan [options]",
      options: [
        { flag: "-out=path", description: "Save the plan to a file for later execution" },
        { flag: "-var 'name=value'", description: "Set a variable value on the command line" },
        { flag: "-var-file=path", description: "Load variable values from a file" },
        { flag: "-target=resource", description: "Target specific resources for planning" },
        { flag: "-destroy", description: "Create a plan to destroy all resources" }
      ],
      examples: [
        { code: "terraform plan", description: "Create a plan for the current configuration" },
        { code: "terraform plan -out=myplan.tfplan", description: "Save the plan to a file" },
        { code: "terraform plan -var 'region=us-west-2'", description: "Plan with a specific variable value" }
      ]
    }
  },
  {
    title: "Applying Changes",
    description: "Once you've reviewed the plan, you can apply the changes to create or update your infrastructure.",
    task: "Enter the command to apply the Terraform configuration.",
    hint: "This command creates or updates infrastructure according to your configuration files.",
    example: "terraform <command>",
    answer: ["terraform apply", "terraform apply -auto-approve"],
    tips: [
      "This command will prompt for confirmation unless you use the -auto-approve flag",
      "It will create a state file to track the resources it manages"
    ],
    commandRef: {
      command: "terraform apply",
      description: "Applies the changes required to reach the desired state of the configuration. This command creates, updates, or destroys infrastructure resources as needed.",
      syntax: "terraform apply [options] [plan]",
      options: [
        { flag: "-auto-approve", description: "Skip the interactive approval prompt" },
        { flag: "-var 'name=value'", description: "Set a variable value on the command line" },
        { flag: "-var-file=path", description: "Load variable values from a file" },
        { flag: "-target=resource", description: "Target specific resources for applying" },
        { flag: "-parallelism=n", description: "Limit the number of concurrent operations" }
      ],
      examples: [
        { code: "terraform apply", description: "Apply changes with interactive confirmation" },
        { code: "terraform apply -auto-approve", description: "Apply changes without confirmation prompt" },
        { code: "terraform apply myplan.tfplan", description: "Apply a previously saved plan file" }
      ]
    }
  },
  {
    title: "Working with State",
    description: "Terraform keeps track of your resources in a state file. You can list the resources it's managing.",
    task: "Enter the command to list all resources in the Terraform state.",
    hint: "This command shows all resources that Terraform is currently managing.",
    example: "terraform state <command>",
    answer: "terraform state list",
    tips: [
      "The state file is crucial for Terraform to know what it's managing",
      "You can use other state subcommands to manipulate the state if needed"
    ],
    commandRef: {
      command: "terraform state list",
      description: "Lists all resources currently tracked in the Terraform state file. This shows the resource addresses that Terraform is managing.",
      syntax: "terraform state list [options] [address...]",
      options: [
        { flag: "-state=path", description: "Path to a state file to read (default: terraform.tfstate)" },
        { flag: "-id=id", description: "Filter resources by their ID attribute" }
      ],
      examples: [
        { code: "terraform state list", description: "List all resources in the state" },
        { code: "terraform state list aws_instance", description: "List all AWS instances in the state" },
        { code: "terraform state list module.vpc", description: "List all resources in a specific module" }
      ]
    }
  },
  {
    title: "Destroying Infrastructure",
    description: "When you no longer need your infrastructure, you can destroy it completely.",
    task: "Enter the command to destroy all resources managed by Terraform.",
    hint: "This command removes all resources defined in your Terraform configuration.",
    example: "terraform <command>",
    answer: ["terraform destroy", "terraform destroy -auto-approve"],
    tips: [
      "This is a destructive operation and can't be undone",
      "Like apply, it will prompt for confirmation unless you use -auto-approve"
    ],
    commandRef: {
      command: "terraform destroy",
      description: "Destroys all resources managed by the current Terraform configuration. This is a destructive operation that permanently removes infrastructure.",
      syntax: "terraform destroy [options]",
      options: [
        { flag: "-auto-approve", description: "Skip the interactive approval prompt" },
        { flag: "-target=resource", description: "Target specific resources for destruction" },
        { flag: "-var 'name=value'", description: "Set a variable value on the command line" },
        { flag: "-var-file=path", description: "Load variable values from a file" }
      ],
      examples: [
        { code: "terraform destroy", description: "Destroy all resources with confirmation prompt" },
        { code: "terraform destroy -auto-approve", description: "Destroy all resources without confirmation" },
        { code: "terraform destroy -target=aws_instance.web", description: "Destroy only a specific resource" }
      ]
    }
  },
  {
    title: "Terraform Validate",
    description: "You can check if your Terraform configuration is valid without initializing a working directory.",
    task: "Write the command to validate your Terraform configuration syntax.",
    hint: "This checks the syntax of your configuration files but doesn't check provider requirements.",
    example: "terraform <command>",
    answer: "terraform validate",
    tips: [
      "This is a quick way to check for syntax errors",
      "It doesn't validate against actual provider APIs or resources"
    ],
    commandRef: {
      command: "terraform validate",
      description: "Validates the Terraform configuration files in the current directory for syntax errors and internal consistency, without accessing remote services.",
      syntax: "terraform validate [options]",
      options: [
        { flag: "-json", description: "Output validation results in JSON format" },
        { flag: "-no-color", description: "Disable color output in the terminal" }
      ],
      examples: [
        { code: "terraform validate", description: "Validate configuration files in the current directory" },
        { code: "terraform validate -json", description: "Validate and output results in JSON format" }
      ]
    }
  },
  {
    title: "Terraform Providers",
    description: "Terraform uses providers to interact with cloud providers, SaaS providers, and other APIs.",
    task: "Enter the command to list the providers used in your current configuration.",
    hint: "This command shows the providers required by your configuration.",
    example: "terraform providers <command>",
    answer: "terraform providers",
    tips: [
      "This shows both configured and implied providers",
      "Providers are downloaded during the terraform init phase"
    ],
    commandRef: {
      command: "terraform providers",
      description: "Shows the providers required by the current configuration, including their version constraints and where they are sourced from.",
      syntax: "terraform providers [options] [config-path]",
      options: [
        { flag: "lock", description: "Write out dependency locks for providers" },
        { flag: "mirror", description: "Save local copies of all required provider plugins" },
        { flag: "schema", description: "Show schemas for the providers in this configuration" }
      ],
      examples: [
        { code: "terraform providers", description: "List all providers required by the configuration" },
        { code: "terraform providers lock", description: "Generate or update the dependency lock file" },
        { code: "terraform providers schema -json", description: "Output provider schemas in JSON format" }
      ]
    }
  },
  {
    title: "Terraform Format",
    description: "Terraform has a built-in formatter to maintain consistent style in configuration files.",
    task: "Enter the command to format all Terraform configuration files in the current directory.",
    hint: "This command rewrites configuration files to a canonical format and style.",
    example: "terraform <command>",
    answer: ["terraform fmt", "terraform fmt ."],
    tips: [
      "This helps maintain consistent style across a team",
      "You can specify individual files or directories"
    ],
    commandRef: {
      command: "terraform fmt",
      description: "Automatically formats Terraform configuration files to a canonical format and style, ensuring consistency across your codebase.",
      syntax: "terraform fmt [options] [target...]",
      options: [
        { flag: "-check", description: "Check if files are formatted, return non-zero exit if not" },
        { flag: "-diff", description: "Display diffs of formatting changes" },
        { flag: "-recursive", description: "Process files in subdirectories recursively" },
        { flag: "-write=false", description: "Don't write changes to files (display only)" }
      ],
      examples: [
        { code: "terraform fmt", description: "Format all .tf files in the current directory" },
        { code: "terraform fmt -recursive", description: "Format all .tf files recursively" },
        { code: "terraform fmt -check", description: "Check formatting without making changes" }
      ]
    }
  },
  {
    title: "Terraform Workspaces",
    description: "Workspaces allow you to manage multiple distinct sets of infrastructure resources with the same configuration.",
    task: "Write the command to list all Terraform workspaces.",
    hint: "This shows all workspaces in the current working directory.",
    example: "terraform workspace <command>",
    answer: "terraform workspace list",
    tips: [
      "The default workspace is always present",
      "Workspaces are useful for managing different environments"
    ],
    commandRef: {
      command: "terraform workspace list",
      description: "Lists all existing workspaces in the current working directory. The current workspace is marked with an asterisk (*).",
      syntax: "terraform workspace list",
      options: [],
      examples: [
        { code: "terraform workspace list", description: "List all workspaces and show the current one" }
      ]
    }
  },
  {
    title: "Creating a Workspace",
    description: "You can create new workspaces for separate environments or configurations.",
    task: "Write the command to create a new workspace named 'development'.",
    hint: "Use the workspace command with a subcommand to create a new workspace.",
    example: "terraform workspace new <name>",
    answer: "terraform workspace new development",
    tips: [
      "This will also switch to the new workspace after creating it",
      "Workspace names should be meaningful, like 'staging', 'production', etc."
    ],
    commandRef: {
      command: "terraform workspace new",
      description: "Creates a new workspace with the given name and switches to it. Each workspace maintains its own state file independently.",
      syntax: "terraform workspace new [options] <name>",
      options: [
        { flag: "-state=path", description: "Copy an existing state file into the new workspace" },
        { flag: "-lock=false", description: "Don't hold a state lock during the operation" }
      ],
      examples: [
        { code: "terraform workspace new development", description: "Create a new workspace named 'development'" },
        { code: "terraform workspace new staging", description: "Create a new workspace named 'staging'" },
        { code: "terraform workspace new -state=old.tfstate prod", description: "Create workspace from existing state" }
      ]
    }
  },
  {
    title: "Switching Workspaces",
    description: "You can switch between existing workspaces to work with different environments.",
    task: "Write the command to switch to a workspace named 'production'.",
    hint: "Use the workspace command with a subcommand to select a different workspace.",
    example: "terraform workspace <command> <name>",
    answer: "terraform workspace select production",
    tips: [
      "This changes which state file is used for operations",
      "Make sure the workspace exists before trying to select it"
    ],
    commandRef: {
      command: "terraform workspace select",
      description: "Switches to an existing workspace by name. All subsequent Terraform operations will use the state associated with the selected workspace.",
      syntax: "terraform workspace select <name>",
      options: [],
      examples: [
        { code: "terraform workspace select production", description: "Switch to the 'production' workspace" },
        { code: "terraform workspace select default", description: "Switch back to the default workspace" }
      ]
    }
  },
  {
    title: "Terraform Version",
    description: "You can check which version of Terraform you're using.",
    task: "Enter the command to display the Terraform version.",
    hint: "This shows the current version of Terraform CLI.",
    example: "terraform <command>",
    answer: ["terraform version", "terraform -version", "terraform --version"],
    tips: [
      "This also shows the provider versions if you've initialized the directory",
      "Different Terraform versions may have different features or syntax"
    ],
    commandRef: {
      command: "terraform version",
      description: "Displays the current version of Terraform CLI and any installed providers. Useful for debugging and ensuring version compatibility.",
      syntax: "terraform version [options]",
      options: [
        { flag: "-json", description: "Output version information in JSON format" }
      ],
      examples: [
        { code: "terraform version", description: "Display Terraform and provider versions" },
        { code: "terraform -version", description: "Short form to display version" },
        { code: "terraform version -json", description: "Output version info in JSON format" }
      ]
    }
  },
  {
    title: "Terraform Output",
    description: "Outputs allow you to extract information from your Terraform state.",
    task: "Write the command to show all outputs from the Terraform configuration.",
    hint: "This displays all outputs defined in your configuration.",
    example: "terraform <command>",
    answer: "terraform output",
    tips: [
      "You can query specific outputs with terraform output [name]",
      "Outputs are useful for sharing information between Terraform modules"
    ],
    commandRef: {
      command: "terraform output",
      description: "Reads and displays output values from the Terraform state file. Outputs are defined in your configuration and can expose useful information about your infrastructure.",
      syntax: "terraform output [options] [name]",
      options: [
        { flag: "-json", description: "Output values in JSON format" },
        { flag: "-raw", description: "Output raw string value without quotes (single value only)" },
        { flag: "-state=path", description: "Path to the state file to read" }
      ],
      examples: [
        { code: "terraform output", description: "Show all output values" },
        { code: "terraform output instance_ip", description: "Show a specific output value" },
        { code: "terraform output -json", description: "Show all outputs in JSON format" }
      ]
    }
  },
  {
    title: "Terraform Graph",
    description: "Terraform can generate a visual representation of your infrastructure.",
    task: "Write the command to generate a dependency graph of your Terraform resources.",
    hint: "This creates a graph in DOT format, which can be visualized with tools like Graphviz.",
    example: "terraform <command>",
    answer: "terraform graph",
    tips: [
      "You can pipe the output to a file: terraform graph > graph.dot",
      "Use tools like Graphviz to convert the DOT file to an image"
    ],
    commandRef: {
      command: "terraform graph",
      description: "Generates a visual representation of the configuration or execution plan in DOT format, showing resource dependencies and relationships.",
      syntax: "terraform graph [options]",
      options: [
        { flag: "-type=plan", description: "Type of graph: plan, plan-refresh-only, plan-destroy, apply" },
        { flag: "-draw-cycles", description: "Highlight any cycles in the graph with colored edges" },
        { flag: "-plan=path", description: "Generate graph from a saved plan file" }
      ],
      examples: [
        { code: "terraform graph", description: "Generate a dependency graph in DOT format" },
        { code: "terraform graph > graph.dot", description: "Save the graph to a file" },
        { code: "terraform graph | dot -Tpng > graph.png", description: "Generate a PNG image using Graphviz" }
      ]
    }
  },
  {
    title: "Terraform Refresh",
    description: "You can update the state file to match the real infrastructure without making changes.",
    task: "Write the command to refresh the state file.",
    hint: "This reads the current settings from all managed resources.",
    example: "terraform <command>",
    answer: "terraform refresh",
    tips: [
      "This is useful when the actual infrastructure might have changed outside of Terraform",
      "This command is now deprecated in favor of terraform apply -refresh-only"
    ],
    commandRef: {
      command: "terraform refresh",
      description: "Updates the state file to match the real-world infrastructure. This reconciles what Terraform knows with what actually exists. Note: deprecated in favor of 'terraform apply -refresh-only'.",
      syntax: "terraform refresh [options]",
      options: [
        { flag: "-var 'name=value'", description: "Set a variable value on the command line" },
        { flag: "-var-file=path", description: "Load variable values from a file" },
        { flag: "-target=resource", description: "Target specific resources for refresh" }
      ],
      examples: [
        { code: "terraform refresh", description: "Refresh state for all resources" },
        { code: "terraform apply -refresh-only", description: "Modern alternative to terraform refresh" },
        { code: "terraform refresh -target=aws_instance.web", description: "Refresh state for a specific resource" }
      ]
    }
  },
  {
    title: "Terraform Console",
    description: "Terraform provides an interactive console for evaluating expressions.",
    task: "Write the command to start the Terraform console.",
    hint: "This opens an interactive command-line for evaluating Terraform expressions.",
    example: "terraform <command>",
    answer: "terraform console",
    tips: [
      "This is useful for testing functions and expressions",
      "You can evaluate variables, functions, and resource attributes"
    ],
    commandRef: {
      command: "terraform console",
      description: "Starts an interactive console for evaluating Terraform expressions, testing functions, and exploring your configuration's data.",
      syntax: "terraform console [options]",
      options: [
        { flag: "-state=path", description: "Path to a state file to load" },
        { flag: "-var 'name=value'", description: "Set a variable value for the console session" },
        { flag: "-var-file=path", description: "Load variable values from a file" }
      ],
      examples: [
        { code: "terraform console", description: "Start an interactive console session" },
        { code: "echo 'upper(\"hello\")' | terraform console", description: "Evaluate an expression non-interactively" },
        { code: "terraform console -var 'env=prod'", description: "Start console with a variable set" }
      ]
    }
  },
  {
    title: "Terraform Import",
    description: "You can import existing infrastructure into Terraform management.",
    task: "Write the command to import an existing AWS EC2 instance with ID 'i-1234567890abcdef0' into a resource named 'aws_instance.example'.",
    hint: "Use the import command with a resource address and the ID of the real-world resource.",
    example: "terraform import [address] [ID]",
    answer: "terraform import aws_instance.example i-1234567890abcdef0",
    tips: [
      "You need to define the resource in your configuration files first",
      "This only imports the resource into state, it doesn't generate configuration"
    ],
    commandRef: {
      command: "terraform import",
      description: "Imports existing infrastructure resources into Terraform state, allowing Terraform to manage resources that were created outside of Terraform.",
      syntax: "terraform import [options] <address> <id>",
      options: [
        { flag: "-var 'name=value'", description: "Set a variable value on the command line" },
        { flag: "-var-file=path", description: "Load variable values from a file" },
        { flag: "-config=path", description: "Path to directory of Terraform configuration files" },
        { flag: "-allow-missing-config", description: "Allow import when no resource configuration exists" }
      ],
      examples: [
        { code: "terraform import aws_instance.web i-1234567890abcdef0", description: "Import an EC2 instance" },
        { code: "terraform import aws_s3_bucket.data my-bucket-name", description: "Import an S3 bucket" },
        { code: "terraform import module.vpc.aws_vpc.main vpc-abc123", description: "Import a resource inside a module" }
      ]
    }
  },
  {
    title: "Terraform State Show",
    description: "You can inspect the details of a specific resource in your state.",
    task: "Write the command to show the attributes of a resource named 'aws_instance.server'.",
    hint: "Use the state command with a subcommand to display a specific resource.",
    example: "terraform state show [address]",
    answer: "terraform state show aws_instance.server",
    tips: [
      "This shows all attributes of the resource as stored in the state file",
      "This is useful for debugging or extracting information"
    ],
    commandRef: {
      command: "terraform state show",
      description: "Displays detailed information about a single resource in the Terraform state, including all its attributes and metadata.",
      syntax: "terraform state show [options] <address>",
      options: [
        { flag: "-state=path", description: "Path to the state file to read" }
      ],
      examples: [
        { code: "terraform state show aws_instance.server", description: "Show details of an EC2 instance" },
        { code: "terraform state show aws_s3_bucket.data", description: "Show details of an S3 bucket" },
        { code: "terraform state show module.vpc.aws_subnet.public[0]", description: "Show a resource with index in a module" }
      ]
    }
  },
  {
    title: "Terraform State Move",
    description: "You can move resources within your state file when you refactor your configuration.",
    task: "Write the command to move 'aws_instance.app' to 'aws_instance.web'.",
    hint: "Use the state command with a subcommand to move resources within the state.",
    example: "terraform state mv [source] [destination]",
    answer: "terraform state mv aws_instance.app aws_instance.web",
    tips: [
      "This is useful when renaming resources or modules",
      "This operation doesn't affect the actual infrastructure, only the state"
    ],
    commandRef: {
      command: "terraform state mv",
      description: "Moves a resource from one address to another within the state file. Useful for refactoring configurations without destroying and recreating resources.",
      syntax: "terraform state mv [options] <source> <destination>",
      options: [
        { flag: "-state=path", description: "Path to the state file to modify" },
        { flag: "-state-out=path", description: "Path to write the updated state (defaults to -state)" },
        { flag: "-dry-run", description: "Preview the changes without modifying state" }
      ],
      examples: [
        { code: "terraform state mv aws_instance.app aws_instance.web", description: "Rename a resource" },
        { code: "terraform state mv module.old module.new", description: "Rename a module" },
        { code: "terraform state mv 'aws_instance.web[0]' 'aws_instance.web[1]'", description: "Move indexed resources" }
      ]
    }
  },
  {
    title: "Terraform State Remove",
    description: "You can remove items from the Terraform state without destroying the actual resources.",
    task: "Write the command to remove 'aws_s3_bucket.logs' from the state.",
    hint: "Use the state command with a subcommand to remove a resource from state.",
    example: "terraform state rm [address]",
    answer: "terraform state rm aws_s3_bucket.logs",
    tips: [
      "This doesn't destroy the resource, it just removes it from Terraform management",
      "Be careful as Terraform will try to recreate the resource on the next apply"
    ],
    commandRef: {
      command: "terraform state rm",
      description: "Removes a resource from the Terraform state without destroying the actual infrastructure. The resource will no longer be managed by Terraform.",
      syntax: "terraform state rm [options] <address>...",
      options: [
        { flag: "-state=path", description: "Path to the state file to modify" },
        { flag: "-dry-run", description: "Preview the changes without modifying state" }
      ],
      examples: [
        { code: "terraform state rm aws_s3_bucket.logs", description: "Remove an S3 bucket from state" },
        { code: "terraform state rm aws_instance.web aws_instance.api", description: "Remove multiple resources" },
        { code: "terraform state rm 'module.app.aws_instance.server[0]'", description: "Remove a resource from a module" }
      ]
    }
  }
];

// Expand the base lessons to ~100 items by repeating them
const terraformLessons = [];
for (let i = 0; i < 100; i++) {
  terraformLessons.push({ ...baseTerraformLessons[i % baseTerraformLessons.length] });
}

export default terraformLessons;
